import { Component, OnInit } from '@angular/core';
import { FormGroupToObject } from '../../pipes/form-group-to-object.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProductService } from '../../services/product.service';
import { Product, ProductPartner, ProductVariants, SuperProduct } from '../../models/product.schema';
import { PartnerService } from '../../services/partner.service';
import { AuthStatusService } from '../../services/auth-status.service';
import { plainToClass } from 'class-transformer';
import { ProductCategory } from 'src/app/models/product-category.schema';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { AutoGeneratedSkuService } from 'src/app/helpers/autoGeneratedSku.service';
import { CommanUsesService } from './../../services/comman-uses.service';
import { OpenaiService } from 'src/app/services/openai.service';
import { Partner } from 'src/app/models/partner.schema';
import { NewProductImageView, ProductImageSyncResponse, ProductImageView } from 'src/app/models/product-image.model';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss']
})
export class ProductFormComponent implements OnInit {
  public groups: string[];
  public partners: any=[];
  public readonly formValidationMsgs;
  public _productForm: any;
  public product: Product;
  public removeFile: boolean;
  public user_extra: {};
  public tmpBtnDisabled: boolean;
  productCategories: ProductCategory[] = [];
  allProductCategories: ProductCategory[] = [];
  imagePath: string;
  products: Product[] = [];
  selectedProductCategory: ProductCategory;
  productCategoryMetaFields: any[] = [];
  summerNoteConfig = {
    placeholder: '',
    tabsize: 2,
    height: '200px',
    uploadImagePath: '/api/upload',
    disableDragAndDrop: true,
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      ['style', ['bold', 'italic', 'underline', 'clear', 'heading', 'style']],
      ['font', ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'clear']],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'link', 'hr']]
    ],
    fontNames: ['Helvetica', 'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Roboto', 'Times']
  }
  aiButtonDisable: boolean = false;
  selectedPartner:  Partner;
  storedShopifyImages: ProductImageView[] = [];
  shopifyImageFiles: NewProductImageView[] = [];
  shopifyImageErrors: string[] = [];
  shopifyImageResults: any[] = [];
  shopifyImageRevision = 0;
  isDraggingShopifyImages = false;
  isShopifyImageUploading = false;
  readonly maxShopifyImageCount = 20;
  readonly maxShopifyImageSize = 15 * 1024 * 1024;
  readonly allowedShopifyImageTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authStatusService: AuthStatusService,
    private _snackBar: MatSnackBar,
    private productService: ProductService,
    private partnerService: PartnerService,
    private productCategoryService: ProductCategoryService,
    private spinnerService: SpinnerService,
    private fb: FormBuilder,
    private autoGeneratedSkuService: AutoGeneratedSkuService,
    private commanUsesService: CommanUsesService,
    private openAIService: OpenaiService
  ) {
    this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
    this.formValidationMsgs = Product.validationMessages;
    this.user_extra = this.authStatusService.getExtra();
  }

  async ngOnInit() {
    try {
      this.spinnerService.show();
      this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
      if(this.groups.includes('admin') || this.groups.includes('operation')){
        this.partners = (await this.partnerService.getAllActivePartnersForDelivery()).partners;
        this.products = (await this.productService.getAllActiveProductsForDelivery()).products;
      }

      //change by j dev
      this.allProductCategories = await this.productCategoryService.getAllProductCategories();
      this.productCategories = [...this.allProductCategories];
      ////

      await this.initialize();
      await this.loadProductImages();
      if (this.product) {
        this.products = this.products.filter(product => product.id !== this.product.id);
      }

      this._productForm.get('controlStock')?.valueChanges.subscribe((value) => {
        this.updateProductAvailabilityValidation(value);
      });

      if (this.groups.includes('partner')) {
        try {
          const loggedInUserInfo = await this.authStatusService.getTokenInfo();
          const loggedInPartner = await this.partnerService.getOne(loggedInUserInfo['extraId']);
          this.selectedPartner = loggedInPartner;
          this.applyPartnerCategoryFilter(this.selectedPartner);
          this.updateAvailabilityValidators(this.selectedPartner.partnerHasWarehouse);
        } catch (error) {
          console.error('Error fetching partner data', error);
        }
      }
    }
    catch(error) {
      this.commanUsesService.showSnackBar("Something went wrong on this page");
      console.log("ERROR", error);
      this.groups = [];
    } finally {
      this.spinnerService.hide();
    }
  }

  private handleAlternateProductNameValidation(): void {
    const isAlternateCtrl = this._productForm.get('isAlternateProductName');
    const alternateNameCtrl = this._productForm.get('alternateProductName');

    const updateValidation = (checked: boolean): void => {
      if (checked) {
        alternateNameCtrl?.setValidators([Validators.required]);
      } else {
        alternateNameCtrl?.clearValidators();
        alternateNameCtrl?.setValue('');
      }

      alternateNameCtrl?.updateValueAndValidity();
    };

    updateValidation(isAlternateCtrl?.value);

    isAlternateCtrl?.valueChanges.subscribe((checked: boolean) => {
      updateValidation(checked);
    });
  }

  updateAvailabilityValidators(isWarehouse: boolean): void {
    const variantsArray = this._productForm.get('productVariants') as FormArray;

    variantsArray.controls.forEach((variantGroup: FormGroup) => {
      const variantAvailabilityControl = variantGroup.get('variantAvailability');

      if (isWarehouse) {
        variantAvailabilityControl?.setValidators([Validators.required, Validators.min(0)]);
      } else {
        variantAvailabilityControl?.setValidators([Validators.min(0)]);
      }

      variantAvailabilityControl?.updateValueAndValidity();
    });

    const productAvailabilityControl = this._productForm.get('productAvailability');
    if (isWarehouse) {
      productAvailabilityControl?.setValidators([Validators.required, Validators.min(0)]);
    } else {
      productAvailabilityControl?.setValidators([Validators.min(0)]);
    }

    productAvailabilityControl?.updateValueAndValidity();
  }

  updateProductAvailabilityValidation(controlStockValue: boolean) {
    const productAvailabilityControl = this._productForm.get('productAvailability');
    if (controlStockValue) {
      productAvailabilityControl?.setValidators([
        Validators.required,
        Validators.min(1),
      ]);
    } else {
      productAvailabilityControl?.clearValidators();
    }
    productAvailabilityControl?.updateValueAndValidity();
  }

  async initialize() {
    this.tmpBtnDisabled = false;
    this._productForm = new FormGroup(Product.validation());
    this.handleAlternateProductNameValidation();
    const productId: string = this.route.snapshot.paramMap.get('id');
    if (productId) {
      try {
        this.product = await this.productService.getOne(Number(productId));
      }
      catch {
        this.router.navigateByUrl("/404", { skipLocationChange: true });
      }
    }
    // if(this.groups.includes('partner')){
    //   this._productForm.controls['productCategory'].setValidators([]);
    //   this._productForm.controls['productCategory'].updateValueAndValidity();
    // }
    if (this.product) {
      this.patch();
    }
    else {
      this.product = new Product();
      if(this.groups.includes('partner')) {
        this._productForm.controls.uniqueProduct.setValue(true);
      }
    }

    if(this.product.sku === '' || this.product.sku === undefined){
      let sku = await this.autoGeneratedSkuService.autoGeneratedSku();
      if(sku){
        this._productForm.controls.sku.setValue(sku);
      }
    }
  }

  async delete() {
    if (!confirm("Sei sicuro di voler cancellare questo prodotto?"))
      return;
    let product: { id: number | string, success: boolean } = await this.productService.delete(this.product.id);
    let message = "Cancellazione del prodotto fallita.";
    if (product.success)
      message = "Cancellazione del prodotto avvenuta con successo.";
    this._snackBar.open(message, 'Chiudi', {
      direction: "ltr",
      duration: 2000,
      horizontalPosition: "center",
      politeness: "assertive",
      verticalPosition: "top"
    }).afterDismissed().subscribe(result => {
      if (product.success)
        this.router.navigate(['/prodotti']);
    });
  }

  patch() {
    this.removeFile = false;
    [
      'name', 
      'description', 
      'price', 
      'sku',
      'quantity', 
      'unEditable', 
      'uniqueProduct', 
      'visibleToOtherPartners', 
      'productAvailability', 
      'adminApproval', 
      'inStock', 
      'isSuperProduct', 
      'ggDispMin', 
      'productStatus', 
      'isDeluxyProduct', 
      'isCakeProduct', 
      'isFlowersProduct', 
      'isBusinessProduct', 
      'isDeluxyExperienceProduct',
      'isDeluxyDotComProduct',
      'shopifyPrice',
      'productAdvantageDesc',
      'cakeDescription',
      'flowersDescription',
      'deluxyDescription',
      'businessDescription',
      'deluxyExperienceDescription',
      'deluxyDotComDescription',
      'productHasVariants',
      'productOptionTitle',
      'notPhysical',
      'isSuperProvinceProduct',
      'controlStock',
      'isAlternateProductName',
      'alternateProductName',
    ].forEach(param => {
      this._productForm.controls[param].setValue(this.product[param]);
    });

    if (this.product['partner'])
      this._productForm.controls.partner.setValue(this.product['partner'].id);
    if (this.product.category)
      this._productForm.controls.category.setValue(this.product.category.join('; '));
    if (this.product.stock)
      this._productForm.controls.stock.setValue(this.product.stock.join('; '));
    if (this.product.line)
      this._productForm.controls.line.setValue(this.product.line.join('; '));
    if (this.product.productCategory){
      this._productForm.controls.productCategory.setValue(this.product?.productCategory?.id);
      this.selectedProductCategory = this.productCategories.find(c => c.id === this.product?.productCategory?.id);
      if(this.selectedProductCategory.categoryMetaFields){
        this.patchProductCategoryMetaFields(this.product.productCategoryMetaFields);
      }
    }
    if(this.product.image){
      this.imagePath = this.product.image;
    }
    if(this.product?.productPartners?.length > 0){
      this.patchProductsPartner();
    }
    if(this.product?.superProducts?.length > 0){
      this.patchSuperProducts();
    }
    if(this.product?.productVariants?.length > 0){
      this.patchProductVariants(this.product.productVariants);
    }
    if(this.product['partner'] && this.product['partner'].id) {
      this.selectedPartner = this.partners.find((p) => p.id === Number(this.product['partner'].id));

      //change by j dev
      this.applyPartnerCategoryFilter(this.selectedPartner);
      ////
    }
  } 

  patchProductsPartner(){
    const control = <FormArray>this._productForm.get('productPartners');
    control.clear();
    if (!this.product.productPartners || Array.isArray(this.product.productPartners) && this.product.productPartners.length == 0){
      this.product.productPartners = [new ProductPartner()];
    }
    this.product.productPartners.forEach((f, index) => {
      control.push(this.patchValues(f, index));
    });
  }

  patchValues(productPartners: ProductPartner, index: number) {
    let productPartner = Product.productPartnerFormGroup();
    productPartner.controls.partner.setValue(
      { 
        id: productPartners?.partner?.id, 
      }
    );
        
    return productPartner;
  }

  patchProductCategoryMetaFields(productCategoryMetaFields: any) {
    let metaFields: any[] = productCategoryMetaFields ? JSON.parse(productCategoryMetaFields) : [];
    this.populateMetaFields(metaFields);
  }

  onSubmit() {
    try {
      this.tmpBtnDisabled = true;
      let product: Product = new FormGroupToObject().transform(this._productForm);
      let image = product.image;
      if(this.groups.includes('partner')){
        product['partner'] = this.user_extra['id'];
        
      }
      if (!this.removeFile) delete product.image;
      delete product.image;

      product = plainToClass(Product, product);
      let promiseResult: any;
      let createType: boolean = true;
      // delete product.image;
      if(!this._productForm.get('partner').value){
        delete product.partner
      }

      if(this.groups.includes('partner')){
        product.partner = this.user_extra['id'];
        delete product?.productPartners;
        delete product?.superProducts;
      }
  
      if(product.productCategoryMetaFields){
        product.productCategoryMetaFields = product.productCategoryMetaFields ? JSON.stringify(product.productCategoryMetaFields) : null;
      }
      if(this.groups.includes('admin') || this.groups.includes('operation')){
        if(product?.productPartners[0]?.partner?.id === null || product?.productPartners[0]?.partner?.id === undefined || product?.productPartners[0]?.partner?.id === ''){
          delete product?.productPartners;
        }
      }

      if(this.groups.includes('admin') || this.groups.includes('operation')){
        if(product?.superProducts[0]?.product?.id === null || product?.superProducts[0]?.product?.id === undefined || product?.superProducts[0]?.product?.id === ''){
          delete product?.superProducts;
        }
      }

      /*
      if(this.groups.includes('admin') || this.groups.includes('operation')){
        if(!product?.productVariants.length){
          delete product?.productVariants;
        }
      }
      */
      // console.log("product", product); return;

      if (this.product['id']) {
        promiseResult = this.productService.update(this.product['id'], product);
        createType = false;
      }
      else
        promiseResult = this.productService.create(product);
      promiseResult
      .then(async (data: Product) => {
        let allGood = true;
        this.product = await this.productService.getOne(data?.id);
        if (image){
          this.product = await this.uploadImage(this.product.id).catch((err) => {
            this.errorMessage(createType, err);
            allGood = false;
          });
        } if(this.removeFile) {
          this.product = await this.productService.updateProductPhoto(this.product.id, null).catch((err) => {
            this.errorMessage(createType, err);
            allGood = false;
          });
        }
        if (allGood) {
          const message: string = `Prodotto ${createType ? 'creato' : 'aggiornato'} con successo`;
          this._snackBar.open(message, 'Chiudi', {
            direction: 'ltr',
            duration: 2000,
            horizontalPosition: 'center',
            politeness: 'assertive',
            verticalPosition: 'top',
          }).afterDismissed().subscribe(result => {
            if (createType)
              this.router.navigate(['/prodotti', this.product.id]);
          });
          this.tmpBtnDisabled = false;
          this.patch();
        }
      })
      .catch((error) => {
        this.tmpBtnDisabled = false;
        this.errorMessage(createType, error);
      });
    } catch (error) {
      console.log("ERROR", error?.message)
    }
  }

  errorMessage(createType, error) {
    let message = `Errore ${createType ? 'nella creazione' : "nell'aggiornamento"} del prodotto`;
    if (error.error && error.error.message === 'DUPLICATE.PRODUCT')
      message = `Errore: esiste già un prodotto con questo nome!`;
    if (error.error && error.error.message === 'DUPLICATE.PRODUCT_SKU')
      message = `Errore: esiste già un prodotto con questo sku!`;
    if(error.error && error.error.message === 'PRODUCT.ONLY_EDITABLE_ADMINISTRATION_SIDE'){
      message = `PRODOTTO SOLO LATO AMMINISTRAZIONE MODIFICABILE`
    }
    this._snackBar.open(message,
      'Chiudi',
      {
        direction: 'ltr',
        duration: 2000,
        horizontalPosition: 'center',
        politeness: 'assertive',
        verticalPosition: 'top',
      }
    );
  }

  onFileSelect(event) {
    if (event.target.files.length > 0) {
      this.removeFile = false;
      const file = event.target.files[0];
      this._productForm.get('image').setValue(file);
    }
  }

  uploadImage(productId: number) {
    return this.productService.updateProductPhoto(
      productId,
      this._productForm.get('image').value
    );
  }

  deleteFile() {
    this.removeFile = true;
  }

  openImage(imageUrl: string) {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  addPartner() {
    const control = <FormArray>this._productForm.get('productPartners');
    control.push(Product.productPartnerFormGroup());
  }

  removePartner(index: number) {
    const control = <FormArray>this._productForm.get('productPartners');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  isPartnerOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const partners of this._productForm.value.productPartners) {
      if (partners.partner.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  patchSuperProducts(){
    const control = <FormArray>this._productForm.get('superProducts');
    control.clear();
    if (!this.product.superProducts || Array.isArray(this.product.superProducts) && this.product.superProducts.length == 0){
      this.product.superProducts = [new SuperProduct()];
    }
    this.product.superProducts.forEach((f, index) => {
      control.push(this.patchSuperProductValues(f, index));
    });
  }

  patchSuperProductValues(superProducts: SuperProduct, index: number) {
    let superProduct = Product.superProductFormGroup();
    superProduct.controls.product.setValue(
      { 
        id: superProducts?.product?.id, 
      }
    );
        
    return superProduct;
  }

  addProduct() {
    const control = <FormArray>this._productForm.get('superProducts');
    control.push(Product.superProductFormGroup());
  }

  removeProduct(index: number) {
    const control = <FormArray>this._productForm.get('superProducts');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  isProductOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const products of this._productForm.value.superProducts) {
      if (products?.product?.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  handlePartnerChange(partnerId: number){
    // if(partnerId){
    //   this.partners = this.partners.filter(p => p.id != partnerId);
    // }
    this.resetCategorySelection();
    this.selectedPartner = this.partners.find((p) => p.id == partnerId);
    this.applyPartnerCategoryFilter(this.selectedPartner);
    if(this.selectedPartner.partnerHasWarehouse) this.updateAvailabilityValidators(this.selectedPartner.partnerHasWarehouse);
  }

  private resetCategorySelection(): void {
    this._productForm.get('productCategory')?.setValue(null);
    this.selectedProductCategory = undefined;
    this.productCategoryMetaFields = [];
    this.metaFields.clear();
  }

  handleProductCategoryChange(categoryId: any){
    this.selectedProductCategory = this.productCategories.find(c => c.id == categoryId);
    this.populateMetaFields();
  }

  private applyPartnerCategoryFilter(partner?: Partner) {
    if (!partner) {
      this.productCategories = [...this.allProductCategories];
      return;
    }

    const ids: number[] =
      partner?.partnerSoldProductCategories
        ?.map((x: any) => x?.productCategory?.id)
        ?.filter((id: any) => typeof id === 'number') || [];

    const allowedIds = new Set<number>(ids);

    if (allowedIds.size === 0) {
      this.productCategories = [...this.allProductCategories];
      return;
    }

    this.productCategories = this.allProductCategories.filter((c) => allowedIds.has(c.id));

    const currentCategoryId = this._productForm?.get('productCategory')?.value;
    if (currentCategoryId && !allowedIds.has(Number(currentCategoryId))) {
      this._productForm.get('productCategory')?.setValue(null);
      this.selectedProductCategory = undefined;
      this.populateMetaFields();
    }
  }

  get metaFields(): FormArray {
    return this._productForm.get('productCategoryMetaFields') as FormArray;
  }

  populateMetaFields(metaFields?: any[]) {
    this.metaFields.clear();
    this.productCategoryMetaFields = this.selectedProductCategory?.categoryMetaFields ? JSON.parse(this.selectedProductCategory.categoryMetaFields) : [];
    this.formValidationMsgs['productCategoryMetaFields'] = [];
    this.productCategoryMetaFields.forEach((field: any, index: number) => {
      const value = metaFields && metaFields[index] ? metaFields[index] : '';
      const control = this.fb.control(value, ((field.type === "1" || field.type === "3") && this.groups.includes('admin') ? Validators.required : []));
      this.metaFields.push(control);
      this.formValidationMsgs[field.fieldName] = [{ type: 'required', message: `${field.fieldName} is Obbligatorio.` }];
    });
  }

  patchProductVariants(productVariantsData: ProductVariants[]){
    const formArray = this.productVariantsFields;
    formArray.clear();
    productVariantsData.forEach(field => {
      formArray.push(new FormGroup({
        id: new FormControl(field.id),
        variantName: new FormControl(field.variantName),
        variantSku: new FormControl(field.variantSku),
        variantPrice: new FormControl(field.variantPrice),
        variantShopifyPrice: new FormControl(field.variantShopifyPrice),
        variantGgDispMin: new FormControl(field.variantGgDispMin),
        variantInStock: new FormControl(field.variantInStock),
        controlVariantStock: new FormControl(field.controlVariantStock),
        variantAvailability: new FormControl(field.variantAvailability, Validators.compose([Validators.min(0)])),
      }));
    });
  }

  get productVariantsFields(): FormArray {
    return this._productForm.get('productVariants') as FormArray;
  }

  async addProductVariant() {
    const control = this._productForm.get('productVariants') as FormArray;
    const newVariantGroup = new FormGroup(ProductVariants.validation());
    let productSku = this._productForm.get('sku').value;
    const index = control.length;
    const indexedSku = `${productSku}-${index + 1}`;
    control.push(newVariantGroup);
    newVariantGroup.get('variantSku')?.setValue(indexedSku);
    if(this.selectedPartner.partnerHasWarehouse) this.updateAvailabilityValidators(this.selectedPartner.partnerHasWarehouse);
  }

  removeProductVariant(index: number) {
    const control = this._productForm.get('productVariants') as FormArray;
    control.removeAt(index);
    if (control.length > 1) {
    }
  }

  handleShopCheckbox(event: any, shopName: string) {
    if (!event.target.checked) return;
  
    const keyPointsMap = {
      deluxy: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li><b>Consegna</b>: in guanti bianchi, a casa o dove vuoi tu</li>`,
      cake: `
        <li><b>Personalizza</b>: scegli forma, gusti e topping</li>
        <li>Consegna dove vuoi tu nel mondo</li>`,
      flowers: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li>Consegna inclusa dove vuoi tu</li>`,
      business: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li><b>Consegna</b>: in guanti bianchi, a casa o dove vuoi tu</li>`,
      deluxyExperience: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li>Consegna inclusa dove vuoi tu</li>`,
      deluxyDotCom: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li><b>Consegna</b>: in guanti bianchi, a casa o dove vuoi tu</li>`,
    };
  
    const key = shopName;
    if (!keyPointsMap[key]) return;
  
    let text = `
      <ul>
        <li><b>${this.selectedProductCategory.categoryName || ''}</b>: 
        ${this._productForm.get('productAdvantageDesc').value || ''}</li>
        ${keyPointsMap[key]}
      </ul>
      <h6>DESCRIZIONE</h6>
      <p>${this._productForm.get('description').value || ''}</p>
    `.replace(/\s+/g, ' ').trim();
  
    this.productCategoryMetaFields.forEach((metaField, i) => {
      text += `<h6>${metaField.fieldName}</h6><p>${this._productForm.get('productCategoryMetaFields').value[i]}</p>`;
    });
  
    this._productForm.get(`${key}Description`).setValue(text);
  }

  productHasVariant(event: any) {
    const checked: boolean = event.target.checked;
    const productOptionTitleControl = this._productForm.get('productOptionTitle');
  
    if (checked) {
      productOptionTitleControl?.setValidators([Validators.required]);
    } else {
      productOptionTitleControl?.clearValidators();
    }
  
    productOptionTitleControl?.updateValueAndValidity();
  }

  async generateTextWithAi() {
    try {
      this.aiButtonDisable = true;

      /*
      const imageSource: File | string | null = this.resolveImageSource();
      if (!imageSource) {
        this.aiButtonDisable = false;
        this.commanUsesService.showSnackBar("Carica prima un'immagine del prodotto");
        return;
      }

      let imageBase64: string;
      try {
        imageBase64 = await this.toBase64(imageSource);
      } catch (error) {
        this.aiButtonDisable = false;
        console.error('Error converting product image to base64:', error);
        this.commanUsesService.showSnackBar("Impossibile leggere l'immagine del prodotto");
        return;
      }
      */

      if (this.selectedProductCategory.categoryMetaFields) {
        let metaFields = this.selectedProductCategory.categoryMetaFields
          ? JSON.parse(this.selectedProductCategory.categoryMetaFields)
          : [];

        if (metaFields.length) {
          const fieldNames = metaFields.map((item) => item.fieldName);
          const fieldsSection = fieldNames.map((name) => `${name}: `).join('\n');
          const returnFormat = fieldNames.map(name => `"${name}": "Generated description for ${name}"`).join(',\n');
          const prompt = `
            Given the following product details, generate detailed descriptions for each field:
            - Product Name: ${this._productForm.get('name').value}
            - Category Name: ${this.selectedProductCategory.categoryName}
            Fields:
            ${fieldsSection}
            Return the descriptions in a structured JSON object like the following:
            {
              ${returnFormat}
            }
            Make the descriptions informative, detailed, and relevant to the product and category. Ensure that all fields are described, and return valid JSON format.
          `;
          // Use the attached product image as the primary visual reference when writing every field.

          // console.log("Prompt sent:", prompt);

          let result = await this.openAIService.generateTextViaOpenAi(prompt);
          // let result = await this.openAIService.generateTextViaOpenAi(prompt, imageBase64);
          const cleanedResponse = result.choices[0].message.content
            .replace(/\\n/g, '')  // Remove newline characters
            .replace(/\\t/g, '')  // Remove tab characters
            .replace(/\\"/g, '"') // Remove escaped quotes if any

          try {
            const descriptionsObject = JSON.parse(cleanedResponse.trim());
            
            metaFields.forEach((field, index) => {
              const control = this._productForm.get('productCategoryMetaFields').get([index]);
              if (control) {
                const description = descriptionsObject[field.fieldName]?.trim() || 'No description available';
                control.patchValue(description);
              }
            });
            console.log("Generated Descriptions:", descriptionsObject);
          } catch (error) {
            console.error('Error parsing the response as JSON:', error);
          } finally {
            this.aiButtonDisable = false;
          }
        } 
      }
    } catch (error) {
      console.error("Error generating text with AI:", error);
      const message = this.commanUsesService.extractErrorMessage(error);
      this.commanUsesService.showSnackBar(message);
    } finally {
      this.aiButtonDisable = false;
    }
  }
  
  getErrorMessages(): string[] {
    const errorMessages: string[] = [];

    /* ============================
      TOP-LEVEL PRODUCT CONTROLS
      ============================ */
    Object.keys(this._productForm.controls).forEach(key => {
      const control = this._productForm.get(key);
      const controlErrors = control?.errors;

      if (controlErrors && Product.validationMessages[key]) {
        Object.keys(controlErrors).forEach(errorKey => {
          const messageObj = Product.validationMessages[key]
            .find((msg: any) => msg.type === errorKey);

          if (messageObj && !errorMessages.includes(key)) {
            errorMessages.push(key);
          }
        });
      }
    });

    /* ============================
      PRODUCT VARIANTS (FormArray)
    ============================ */
    const variantsArray = this._productForm.get('productVariants') as FormArray;

    if (variantsArray && variantsArray.controls.length > 0) {
      variantsArray.controls.forEach(variantGroup => {

        const group = variantGroup as FormGroup;

        Object.keys(group.controls).forEach(controlKey => {
          const control = group.get(controlKey);
          const errors = control?.errors;

          if (
            errors &&
            Product.validationMessages.productVariants &&
            Product.validationMessages.productVariants[controlKey]
          ) {
            Object.keys(errors).forEach(errorKey => {
              const messageObj =
                Product.validationMessages.productVariants[controlKey]
                  .find((msg: any) => msg.type === errorKey);

              if (messageObj && !errorMessages.includes(controlKey)) {
                errorMessages.push(controlKey);
              }
            });
          }
        });
      });
    }

    /* =========================================
    PRODUCT CATEGORY META FIELDS (DYNAMIC)
    ========================================= */
    const metaFieldsArray = this._productForm.get('productCategoryMetaFields') as FormArray;

    if (metaFieldsArray && this.productCategoryMetaFields?.length) {
      metaFieldsArray.controls.forEach((control, index) => {
        if (control.errors) {
          const fieldName = this.productCategoryMetaFields[index]?.fieldName;

          if (
            fieldName &&
            this.formValidationMsgs[fieldName] &&
            !errorMessages.includes(fieldName)
          ) {
            errorMessages.push(fieldName);
          }
        }
      });
    }

    return errorMessages;
  }


  translateErrorMessage(error: string): string {
    const staticMessages: { [key: string]: string } = {
      name: 'NOME PRODOTTO',
      sku: 'SKU',
      productCategory: 'CATEGORIA',
      price: 'PREZZO',
      shopifyPrice: 'PREZZO SHOPIFY',
      quantity: 'QUANTITÀ',
      image: 'IMMAGINE',
      productAdvantageDesc: 'DESCRIZIONE VANTAGGI',
      productOptionTitle: 'TITOLO OPZIONI',
      productAvailability: 'INVENTARIO',
      alternateProductName: 'NOME ALTERNATIVO DEL PRODOTTO',

      // VARIANTS
      variantName: 'NOME VARIANTE',
      variantSku: 'SKU VARIANTE',
      variantPrice: 'PREZZO VARIANTE',
      variantShopifyPrice: 'PREZZO SHOPIFY VARIANTE',
      variantAvailability: 'INVENTARIO VARIANTE',
    };

    if (staticMessages[error]) {
      return staticMessages[error];
    }

    const dynamicLabel = this.productCategoryMetaFields
      ?.find((f: any) => f.fieldName === error)
      ?.fieldName;

    return dynamicLabel?.toUpperCase() || error;
  }

  async loadProductImages(): Promise<void> {
    if (!this.product?.id) {
      this.storedShopifyImages = [];
      this.shopifyImageRevision = 0;
      return;
    }

    const images = await this.productService.getImages(
      this.product.id,
    );

    this.storedShopifyImages = Array.isArray(images)
      ? images
      : [];

    this.shopifyImageRevision =
      this.storedShopifyImages.length
        ? Math.max(
            ...this.storedShopifyImages.map(
              image => Number(image.revision || 0),
            ),
          )
        : 0;
  }

  canUseShopifyImageUploader(): boolean {
    return (
      !!this.product?.id &&
      this._productForm?.get('adminApproval')?.value === true &&
      this.getSelectedShopifyStores().length > 0
    );
  }

  private getSelectedShopifyStores(): string[] {
    const stores: string[] = [];

    if (this._productForm?.get('isDeluxyProduct')?.value) {
      stores.push('deluxy');
    }

    if (this._productForm?.get('isCakeProduct')?.value) {
      stores.push('cake');
    }

    if (this._productForm?.get('isFlowersProduct')?.value) {
      stores.push('flowers');
    }

    if (this._productForm?.get('isBusinessProduct')?.value) {
      stores.push('business');
    }

    if (this._productForm?.get('isDeluxyExperienceProduct')?.value) {
      stores.push('deluxyexperience');
    }

    if (this._productForm?.get('isDeluxyDotComProduct')?.value) {
      stores.push('deluxydotcom');
    }

    return stores;
  }

  onShopifyImageDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.canUseShopifyImageUploader()) {
      this.isDraggingShopifyImages = true;
    }
  }

  onShopifyImageDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingShopifyImages = false;
  }

  onShopifyImageDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingShopifyImages = false;

    if (!this.canUseShopifyImageUploader()) {
      return;
    }

    const files = event.dataTransfer?.files;

    if (files?.length) {
      this.handleShopifyImageFiles(files);
    }
  }

  onShopifyImageSelect(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files?.length) {
      this.handleShopifyImageFiles(input.files);
    }

    input.value = '';
  }

  private handleShopifyImageFiles(fileList: FileList): void {
    this.shopifyImageErrors = [];
    const files = Array.from(fileList);

    if (
      this.storedShopifyImages.length +
        this.shopifyImageFiles.length +
        files.length >
      this.maxShopifyImageCount
    ) {
      this.shopifyImageErrors.push(
        `Maximum ${this.maxShopifyImageCount} images are allowed.`,
      );
      return;
    }

    for (const file of files) {
      if (!this.allowedShopifyImageTypes.includes(file.type)) {
        this.shopifyImageErrors.push(
          `${file.name}: unsupported image type.`,
        );
        continue;
      }

      if (file.size > this.maxShopifyImageSize) {
        this.shopifyImageErrors.push(
          `${file.name}: maximum size is 15MB.`,
        );
        continue;
      }

      const duplicate = this.shopifyImageFiles.some(
        item =>
          item.file.name === file.name &&
          item.file.size === file.size &&
          item.file.lastModified === file.lastModified,
      );

      if (duplicate) {
        this.shopifyImageErrors.push(
          `${file.name}: already selected.`,
        );
        continue;
      }

      const reader = new FileReader();

      reader.onload = () => {
        this.shopifyImageFiles = [
          ...this.shopifyImageFiles,
          {
            file,
            preview: String(reader.result || ''),
          },
        ];
      };

      reader.readAsDataURL(file);
    }
  }

  removeStoredShopifyImage(
    imageId: number,
    event?: Event,
  ): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.storedShopifyImages =
      this.storedShopifyImages.filter(
        image => image.id !== imageId,
      );
  }

  removeNewShopifyImage(
    index: number,
    event?: Event,
  ): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.shopifyImageFiles =
      this.shopifyImageFiles.filter(
        (_, currentIndex) => currentIndex !== index,
      );
  }

  moveStoredShopifyImage(
    index: number,
    direction: -1 | 1,
    event?: Event,
  ): void {
    event?.preventDefault();
    event?.stopPropagation();

    const targetIndex = index + direction;

    if (
      targetIndex < 0 ||
      targetIndex >= this.storedShopifyImages.length
    ) {
      return;
    }

    const images = [...this.storedShopifyImages];
    const current = images[index];

    images[index] = images[targetIndex];
    images[targetIndex] = current;

    this.storedShopifyImages = images;
  }

  clearNewShopifyImages(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.shopifyImageFiles = [];
    this.shopifyImageErrors = [];
  }

  async syncSelectedProductImagesWithShopify(): Promise<void> {
    if (!this.canUseShopifyImageUploader()) {
      this.commanUsesService.showSnackBar(
        'Save and approve the product, then select at least one Shopify store.',
      );
      return;
    }

    const finalCount =
      this.storedShopifyImages.length +
      this.shopifyImageFiles.length;

    if (
      finalCount === 0 &&
      !confirm('Remove all Shopify images from this product?')
    ) {
      return;
    }

    this.isShopifyImageUploading = true;
    this.shopifyImageResults = [];

    try {
      const formData = new FormData();

      for (const store of this.getSelectedShopifyStores()) {
        formData.append('stores', store);
      }

      formData.append(
        'keepImageIds',
        JSON.stringify(
          this.storedShopifyImages.map(image => image.id),
        ),
      );

      formData.append(
        'revision',
        String(this.shopifyImageRevision),
      );

      for (const item of this.shopifyImageFiles) {
        formData.append(
          'images',
          item.file,
          item.file.name,
        );
      }

      const response: ProductImageSyncResponse =
        await this.productService.syncImages(
          this.product.id,
          formData,
        );

      this.storedShopifyImages = response.images || [];
      this.shopifyImageRevision = response.revision;
      this.shopifyImageResults = response.results || [];
      this.shopifyImageFiles = [];
      this.shopifyImageErrors = [];

      this.commanUsesService.showSnackBar(
        'Product images synchronized.',
      );
    } catch (error) {
      if (error?.status === 409) {
        await this.loadProductImages();
      }

      this.commanUsesService.showSnackBar(
        error?.error?.message ||
          error?.message ||
          'Image synchronization failed.',
      );
    } finally {
      this.isShopifyImageUploading = false;
    }
  }

  async retryShopifyStore(
    storeRecordId: number,
    event?: Event,
  ): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    try {
      await this.productService.retryStore(
        this.product.id,
        storeRecordId,
      );

      await this.loadProductImages();

      this.commanUsesService.showSnackBar(
        'Store synchronization retried.',
      );
    } catch (error) {
      this.commanUsesService.showSnackBar(
        error?.error?.message ||
          'Store retry failed.',
      );
    }
  }

  formatFileSize(size: number): string {
    if (!size && size !== 0) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  getShopDisplayName(storeKey: string): string {
    const labels = {
      deluxy: 'Deluxy',
      cake: 'CakeDesign',
      flowers: 'Flowers',
      business: 'Business',
      deluxyexperience: 'Deluxy Experience',
      deluxydotcom: 'Deluxy.com',
    };

    return labels[storeKey] || storeKey;
  }
}
